import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  CardCvcElement as CardCvcStripeElement,
  CardExpiryElement as CardExpiryStripeElement,
  CardNumberElement as CardNumberStripeElement,
} from '@stripe/react-stripe-js';
import type {
  StripeCardCvcElement,
  StripeCardExpiryElement,
  StripeCardNumberElement,
  StripeElementChangeEvent,
} from '@stripe/stripe-js';
import { Text, TextFieldLabel, theme } from '@frameio/vapor';
import {
  useCardElementOptions,
  useStripeCardElementStyle,
} from '../utils/stripeElementStyles';
import TEXT from './generated/TEXT/BillingStripeCardElements';
import styles from './StripeCardElements.module.css';

type StripeElementType = 'cardNumber' | 'cardExpiry' | 'cardCvc';

TEXT.DEFINE({
  /** Label for the card number input field */
  cardNumber: 'Card number',
  /** Label for the CVC (Card Verification Code) input field */
  cvc: 'CVC',
  /** Label for the card expiration date input field */
  expiration: 'Expiration',
});

const textFieldLabelStyles = {
  display: 'block',
  marginBottom: theme.spaces[6],
};

type StripeCardElementsProps = {
  isEditing: boolean;
  onCardFormCompleteChange: (isComplete: boolean) => void;
};

function ErrorText({ errorMessage }: { errorMessage: string | null }) {
  return (
    <Text
      aria-atomic="true"
      aria-live="assertive"
      color="error"
      role="alert"
      style={{
        opacity: errorMessage ? 1 : 0,
        paddingTop: theme.spaces[4],
        lineHeight: '1',
      }}
      variant="caption"
    >
      {errorMessage}
    </Text>
  );
}

export default function StripeCardElements({
  isEditing = true,
  onCardFormCompleteChange,
}: StripeCardElementsProps) {
  const isCardElementCompleteRef = useRef({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
  });

  const cardNumberElement = useRef<StripeCardNumberElement | null>(null);
  const cardExpiryElement = useRef<StripeCardExpiryElement | null>(null);
  const cardCvcElement = useRef<StripeCardCvcElement | null>(null);
  const [cardNumberError, setCardNumberError] = useState<string | null>(null);
  const [cardExpiryError, setCardExpiryError] = useState<string | null>(null);
  const [cardCvcError, setCardCvcError] = useState<string | null>(null);

  useEffect(() => {
    // Stripe elements aren't unmounted between edits, so clear them manually.
    cardNumberElement.current?.clear();
    cardExpiryElement.current?.clear();
    cardCvcElement.current?.clear();
    if (isEditing) cardNumberElement.current?.focus();
  }, [isEditing]);

  const handleChange = useCallback(
    (event: StripeElementChangeEvent) => {
      const elementType = event.elementType as StripeElementType;

      const ERROR_SETTER: Record<
        StripeElementType,
        (error: string | null) => void
      > = {
        cardNumber: setCardNumberError,
        cardExpiry: setCardExpiryError,
        cardCvc: setCardCvcError,
      };

      if (event.error) {
        ERROR_SETTER[elementType]?.(event.error.message);
      } else {
        ERROR_SETTER[elementType]?.(null);
      }

      if (event.complete !== isCardElementCompleteRef.current[elementType]) {
        isCardElementCompleteRef.current[elementType] = event.complete;

        onCardFormCompleteChange(
          isCardElementCompleteRef.current.cardNumber &&
            isCardElementCompleteRef.current.cardExpiry &&
            isCardElementCompleteRef.current.cardCvc
        );
      }
    },
    [onCardFormCompleteChange]
  );

  const options = useCardElementOptions();
  const cardStyleVars = useStripeCardElementStyle();

  return (
    <div style={cardStyleVars}>
      <div className={styles.cardNumberContainer}>
        <TextFieldLabel style={textFieldLabelStyles}>
          {TEXT.cardNumber}
        </TextFieldLabel>
        <CardNumberStripeElement
          className={styles.stripeElement}
          onChange={handleChange}
          onReady={(element: StripeCardNumberElement) => {
            cardNumberElement.current = element;
          }}
          options={options}
        />
        <ErrorText errorMessage={cardNumberError} />
      </div>
      <div className={styles.cardInputContainer}>
        <div
          className={clsx(styles.cardSideContainer, styles.cardExpiryContainer)}
        >
          <TextFieldLabel style={textFieldLabelStyles}>
            {TEXT.expiration}
          </TextFieldLabel>
          <CardExpiryStripeElement
            className={styles.stripeElement}
            onChange={handleChange}
            onReady={(element: StripeCardExpiryElement) => {
              cardExpiryElement.current = element;
            }}
            options={options}
          />

          <ErrorText errorMessage={cardExpiryError} />
        </div>
        <div
          className={clsx(styles.cardSideContainer, styles.cardCvcContainer)}
        >
          <TextFieldLabel style={textFieldLabelStyles}>
            {TEXT.cvc}
          </TextFieldLabel>
          <CardCvcStripeElement
            className={styles.stripeElement}
            onChange={handleChange}
            onReady={(element: StripeCardCvcElement) => {
              cardCvcElement.current = element;
            }}
            options={options}
          />
          <ErrorText errorMessage={cardCvcError} />
        </div>
      </div>
    </div>
  );
}
